// ============================================================================
// Borradores de formulario (Panel Cliente).
//
// Capa delgada sobre @plataforma/utils que aporta lo propio del panel:
//   - Aísla cada borrador por empresa + usuario + ámbito/contexto, para que
//     dos cuentas (o dos productos, o alta vs. edición) no se pisen nunca en
//     el mismo navegador.
//   - Conecta el ciclo de vida común de un formulario dentro de un <pf-modal>:
//     restaurar al montar, autoguardar en input/change, volcar al cerrar y
//     limpiar al guardar.
//   - Un indicador discreto reutilizable ("Guardando…", "Borrador guardado").
// ============================================================================

import { crearClaveBorrador, crearGestorBorrador } from '@plataforma/utils';

import { obtenerEstadoSesion } from './estado-sesion.js';

function contextoUsuario() {
  const { claims, sesion } = obtenerEstadoSesion();
  const empresaId = claims?.empresa_id ?? 'sin-empresa';
  const usuarioId = sesion?.user?.id ?? 'sin-usuario';
  return `${empresaId}:${usuarioId}`;
}

/**
 * Crea un gestor de borrador ya aislado para el panel.
 *
 * @param {string} ambito - tipo de formulario (ej. 'formulario-producto').
 * @param {object} [opciones]
 * @param {string} [opciones.contexto] - id/variante de entidad (ej. 'editar:<id>').
 * @param {number} [opciones.esperaMs]
 * @param {(estado: 'guardando' | 'guardado' | 'error') => void} [opciones.alEstado]
 */
export function crearGestorBorradorFormulario(ambito, { contexto = '', esperaMs, alEstado } = {}) {
  const sufijo = contexto ? `${ambito}:${contexto}` : ambito;
  const clave = crearClaveBorrador('borrador-panel', contextoUsuario(), sufijo);
  return crearGestorBorrador({ clave, esperaMs, alEstado });
}

const ETIQUETAS_ESTADO = {
  guardando: 'Guardando borrador…',
  guardado: 'Borrador guardado',
  error: 'No se pudo guardar el borrador',
};

/**
 * @param {HTMLElement | null} elemento
 * @param {'guardando' | 'guardado' | 'error' | null} estado
 */
export function aplicarEstadoBorrador(elemento, estado) {
  if (!elemento) return;
  elemento.textContent = ETIQUETAS_ESTADO[estado] ?? '';
}

/**
 * Conecta el borrador a un formulario/modal ya montado.
 *
 * @param {object} opciones
 * @param {HTMLElement} opciones.contenedor - donde viven los campos (form o modal);
 *   recibe el listener de input/change.
 * @param {object} opciones.gestor - gestor devuelto por crearGestorBorrador.
 * @param {() => object} opciones.leerDatos
 * @param {(datos: object) => void} [opciones.aplicarDatos]
 * @param {HTMLElement} [opciones.modal] - <pf-modal> para volcar el borrador al cerrar.
 * @returns {{ restaurado: boolean, datosBorrador: object | null, gestor: object,
 *   finalizar: () => void, descartar: () => void }}
 */
export function conectarBorradorDeFormulario({
  contenedor,
  gestor,
  leerDatos,
  aplicarDatos,
  modal,
}) {
  let ensuciado = false;
  let finalizado = false;

  const borrador = gestor.leer();

  if (borrador?.datos) {
    aplicarDatos?.(borrador.datos);
    ensuciado = true;
  }

  const alEditar = () => {
    ensuciado = true;
    gestor.programar(leerDatos());
  };
  contenedor.addEventListener('input', alEditar);
  contenedor.addEventListener('change', alEditar);

  const alCerrar = () => {
    if (finalizado || !ensuciado) return;
    gestor.volcar(leerDatos());
  };

  // El mismo <pf-modal> (y a veces el mismo contenedor) se reutiliza entre
  // aperturas: se retiran los listeners de la conexión anterior para no
  // acumularlos ni volcar sobre datos de una apertura previa.
  if (modal) {
    const previa = modal._pfBorrador;
    if (previa) {
      previa.contenedor.removeEventListener('input', previa.alEditar);
      previa.contenedor.removeEventListener('change', previa.alEditar);
      modal.removeEventListener('pf-cerrar', previa.alCerrar);
    }
    modal._pfBorrador = { contenedor, alEditar, alCerrar };
    modal.addEventListener('pf-cerrar', alCerrar);
  }

  return {
    restaurado: Boolean(borrador?.datos),
    datosBorrador: borrador?.datos ?? null,
    gestor,
    /** Éxito: limpia el borrador y desactiva el volcado al cerrar. */
    finalizar() {
      finalizado = true;
      ensuciado = false;
      gestor.limpiar();
    },
    /** El usuario descarta el borrador sin guardar el formulario. */
    descartar() {
      ensuciado = false;
      gestor.limpiar();
    },
  };
}
